import { DataType } from "@/types/enums/DataType";
import type { Field } from "@/types/Field";
import { resolveLangKeyFromMap, resolveLangValue } from "@/lib/utils/resolve-lang";
import { SMS_CUSTOM_PREFIX, SMS_EVENT_PLACEHOLDERS, SMS_PLACEHOLDER_ALLOWLIST } from "@/lib/utils/smsPlaceholders";
import type { SmsEventValues } from "@/lib/sms/smsEventValues";

/**
 * "Preview as guest" in the SMS builder (SMS templates wave, owner 27.10.
 * 18:40): which text a real guest gets and how it reads with their values.
 *
 * MIRROR main-consumer `executor/send-sms.ts` `pickSmsText` (guest mode) and
 * `sms-render.ts` `renderSmsBody` — the language chain through the FE's port
 * of the worker's resolver (`resolve-lang.ts`, hand-synced with
 * `main-consumer/src/lib/lang-resolve.ts`), the markers in one pass.
 */

/**
 * The text a guest of `guestLanguage` gets, and the language it is in (the
 * dates of the SMS follow it): exact → same base language → the main
 * language → any text. Only the template's active languages with a
 * non-blank text count (the repo hands the worker non-blank texts only). A
 * non-string language reads as "".
 */
export function smsTextForGuest(
  bodies: Readonly<Record<string, string>>,
  languages: readonly string[],
  primaryLanguage: string,
  guestLanguage: unknown,
): { text: string; lang: string } {
  const map: Record<string, string> = {};
  for (const l of languages) {
    const v = bodies[l];
    if (typeof v === "string" && v.trim() !== "") map[l] = v;
  }
  const lang = typeof guestLanguage === "string" ? guestLanguage : "";
  const primaryText = map[primaryLanguage] ?? "";
  return {
    text: resolveLangValue(map, lang, primaryLanguage, primaryText),
    lang: resolveLangKeyFromMap(map, lang, primaryLanguage) ?? primaryLanguage,
  };
}

/** The markers the sender renders: the allowlist and `additionalFields.<name>` (no dot in the name). */
function rendersMarker(key: string): boolean {
  if ((SMS_PLACEHOLDER_ALLOWLIST as readonly string[]).includes(key)) return true;
  if (!key.startsWith(SMS_CUSTOM_PREFIX)) return false;
  const name = key.slice(SMS_CUSTOM_PREFIX.length);
  return name.length > 0 && !name.includes(".");
}

const EVENT_KEYS: ReadonlySet<string> = new Set(SMS_EVENT_PLACEHOLDERS);

/** A stored value as the sender writes it: text as is, numbers and yes/no as text, anything else (an image, a map) nothing. */
function scalar(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint") return String(v);
  return "";
}

/** The marker regex of the sender: two opening braces, the shortest run, two closing braces; the key is trimmed. */
const MARKER_RE = /\{\{(.*?)\}\}/g;

/**
 * The SMS `body` as this guest gets it: the event's facts (already in the
 * text's language), the guest's own fields and `additionalFields.<name>`.
 * As the sender: one pass, never re-scanned; a marker it does not render, or
 * one without a value, comes out empty; an IMAGE field never renders, and
 * with the field list unknown (`fields` null) no custom field does (fail
 * closed).
 */
export function renderSmsForGuest(
  body: string,
  guest: Readonly<Record<string, unknown>>,
  eventValues: SmsEventValues,
  fields: readonly Field[] | null,
): string {
  const imageFields =
    fields === null
      ? null
      : new Set(
          fields
            .filter((f) => f.dataType === DataType.IMAGE && typeof f.name === "string")
            .map((f) => (f.name as string).toLowerCase()),
        );
  return body.replace(MARKER_RE, (_m, raw: string) => {
    const key = raw.trim();
    if (!rendersMarker(key)) return "";
    if (EVENT_KEYS.has(key)) return (eventValues as Record<string, string | undefined>)[key] ?? "";
    if (key.startsWith(SMS_CUSTOM_PREFIX)) {
      const name = key.slice(SMS_CUSTOM_PREFIX.length);
      if (imageFields === null || imageFields.has(name.toLowerCase())) return "";
      const af = guest.additionalFields;
      if (af === null || typeof af !== "object" || Array.isArray(af)) return "";
      if (!Object.prototype.hasOwnProperty.call(af, name)) return "";
      return scalar((af as Record<string, unknown>)[name]);
    }
    if (!Object.prototype.hasOwnProperty.call(guest, key)) return "";
    return scalar(guest[key]);
  });
}
